import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Section from "@/components/Section";
import AuditCta from "@/components/AuditCta";
import CinematicShell from "@/components/cinematic/CinematicShell";
import { moduleStill } from "@/components/cinematic/stills";
import api from "@/lib/api";
import { ArrowRight, CheckCircle2 } from "lucide-react";

export default function ModulePage() {
  const { code } = useParams();
  const [mod, setMod] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setMod(null); setError("");
    api.get(`/config/modules/${code}`).then((r) => setMod(r.data)).catch(() => setError("Module not found."));
  }, [code]);

  if (error) {
    return (
      <>
        <PublicNav />
        <Section className="py-32 text-center" testId="module-not-found">
          <div className="dt6-empty inline-block">NO SUCH MODULE. Return to <Link className="dt6-link ml-1" to="/">base</Link>.</div>
        </Section>
        <PublicFooter />
      </>
    );
  }

  if (!mod) {
    return (
      <>
        <PublicNav />
        <Section className="py-32 text-center" testId="module-loading">
          <div className="font-mono text-sm text-[color:var(--dt6-muted)] tracking-[0.2em]">INITIALIZING...</div>
        </Section>
      </>
    );
  }

  return (
    <>
      <PublicNav />
      <div className="relative dt6-cinematic-vignette" style={{ minHeight: "520px" }}>
        <CinematicShell
          sceneImport={() => import("@/components/cinematic/scenes/ModuleInspection")}
          sceneProps={{ code: mod.code }}
          stillSrc={moduleStill(mod.code)}
          testId={`module-hero-scene-${mod.code}`}
          className="dt6-cinematic-grain"
          style={{ position: "absolute", inset: 0, zIndex: 0 }}
        />
        <Section className="pt-16 pb-8 relative dt6-cinematic-copy" testId={`module-hero-${mod.code}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="dt6-chip dt6-chip-primary">MODULE {mod.code}</div>
            <div className="font-mono text-[0.7rem] tracking-[0.14em] text-[color:var(--dt6-muted)]">/ {mod.name.toUpperCase()}</div>
          </div>
          <h1 className="font-heading text-5xl md:text-6xl font-black leading-tight">{mod.name}.</h1>
          <p className="mt-4 font-heading text-2xl md:text-3xl text-[color:var(--dt6-muted)] max-w-4xl">
            {mod.tagline}
          </p>
          <p className="mt-6 text-lg text-[color:var(--dt6-muted)] max-w-3xl">{mod.what_it_does}</p>
        </Section>
      </div>

      <Section className="py-8" testId={`module-deliverables-${mod.code}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="dt6-card p-8">
            <div className="dt6-chip mb-4">/ DELIVERABLES</div>
            <ul className="space-y-3">
              {(mod.deliverables || []).map((d, i) => (
                <li key={i} className="flex items-start gap-3 text-sm" data-testid={`mod-${mod.code}-deliv-${i}`}>
                  <CheckCircle2 size={16} strokeWidth={1.5} className="text-[color:var(--dt6-primary)] mt-0.5 shrink-0" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="dt6-card p-8">
            <div className="dt6-chip mb-4">/ USE CASES</div>
            <ul className="space-y-3">
              {(mod.use_cases || []).map((d, i) => (
                <li key={i} className="flex items-start gap-3 text-sm" data-testid={`mod-${mod.code}-usecase-${i}`}>
                  <span className="font-mono text-[0.7rem] tracking-[0.14em] text-[color:var(--dt6-primary)] mt-1">→</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {mod.works_with?.length > 0 && (
        <Section className="py-8" testId={`module-works-with-${mod.code}`}>
          <div className="dt6-chip mb-4">/ WORKS WITH</div>
          <div className="flex flex-wrap gap-3">
            {mod.works_with.map((c) => (
              <Link key={c} to={`/modules/${c}`} className="dt6-card px-4 py-3 hover:border-[color:var(--dt6-primary)]" data-testid={`mod-${mod.code}-works-${c}`}>
                <span className="font-mono text-[0.75rem] tracking-[0.14em] text-[color:var(--dt6-primary)] mr-2">{c}</span>
                Cross-lane
              </Link>
            ))}
          </div>
        </Section>
      )}

      <Section className="py-16" testId={`module-cta-${mod.code}`}>
        <AuditCta context={`module-${mod.code}-cta`} />
      </Section>

      <PublicFooter />
    </>
  );
}
